import { createFileRoute } from "@tanstack/react-router"
import { api } from "~/utils/api"
import { DataTableBaseV2 } from "~/components/reusable/DataTableV2"
import { useMemo, useRef, useState } from "react"
import { type IForecastComparison } from "~/server/api/routers/forecastValidationRoute"
import type { AgGridReact } from "ag-grid-react"
import type { ColDef, GridOptions } from "ag-grid-community"
import { formatNumber } from "~/utils/formatting"
import { formatDate } from "~/utils/datetime"
import { AggregationMethod, AggregationSelector } from "~/components/reusable/AggregationSelector"
import { useSessionStorage } from "usehooks-ts"
import { COMPARISON_TABLE_ID } from "~/constants"
import { first, mean, startCase } from "lodash"
import type { ICollectionDetails } from "~/hooks/useCollections"
import { UniqueListTooltip } from "~/components/reusable/table/tooltips/UniqueListTooltip"
import { getTailwindValue } from "~/utils/tailwind"
import { Module, WorkspacePage } from "~/components/design-system/pages"
import { BreadcrumbWrapper } from "~/components/reusable/Breadcrumb"
import SearchField from "~/components/planning/SearchField"
import { filterOnSearchString } from "~/utils/search"

const aggregationOptions = [
  AggregationMethod.Category,
  AggregationMethod.ProductName,
  AggregationMethod.Variant,
]

export const Route = createFileRoute("/_auth/internal/forecasts/forecast-validation-check")({
  component: RouteComponent,
})

function RouteComponent() {
  const internalGridRef = useRef<AgGridReact<IForecastComparison>>(null)

  const [searchText, setSearchText] = useState("")
  const [aggregation, setAggregation] = useSessionStorage<string>(
    COMPARISON_TABLE_ID,
    AggregationMethod.ProductName,
  )

  const { data: validationChecks, isLoading } =
    api.forecastValidationRule.forecastValidationChecks.useQuery()

  const filteredRowData = useMemo(() => {
    if (!searchText) {
      return validationChecks
    } else if (validationChecks === undefined) {
      return []
    }

    return filterOnSearchString(validationChecks, searchText)
  }, [validationChecks, searchText])

  const defaultColumnDef: ColDef<ICollectionDetails> = useMemo(
    () => ({
      rowGroup: false,
      suppressHeaderMenuButton: true,
      rowGroupIndex: 0,
      wrapHeaderText: true,
      minWidth: 130,
      enableCellChangeFlash: true,
      cellDataType: "text",
    }),
    [],
  )

  const autoGroupColumnDef: ColDef = useMemo(() => {
    return {
      minWidth: 300,
      field: "variantId",
      pinned: "left",
      headerName: startCase(aggregation ?? ""),
      lockPinned: true,
      cellStyle: (params) =>
        !params.node.group || params.node.field === "variantId" || params.node.field === "productId"
          ? { cursor: "pointer" }
          : { cursor: "default" },
      cellRendererSelector: () => {
        return {
          component: "agGroupCellRenderer",
          params: {
            suppressCount: true,
            innerRenderer: (params: any) => {
              const footer = params.node.footer

              if (footer) {
                return <span className="font-semibold">Totals</span>
              }

              return (
                <div className="flex items-center gap-3 h-[40px]">
                  <span className="label-base">{params.value}</span>
                </div>
              )
            },
          },
        }
      },
    }
  }, [aggregation])

  const colDefs: ColDef<IForecastComparison>[] = useMemo(() => {
    return [
      {
        headerName: "Variant ID",
        field: "variantId",
        sortable: true,
        rowGroup: AggregationMethod.Variant === aggregation,
        aggFunc: "unique",
        valueFormatter: (params) => {
          return params.node?.group
            ? params.value?.length > 1
              ? `${params.value?.length} variants`
              : first(params.value)
            : params.value
        },
        tooltipComponent: UniqueListTooltip,
        tooltipComponentParams: { title: "Variants", tooltipType: "UniqueList" },
        tooltip: (params) => params.value,
      },
      {
        headerName: "Product ID",
        field: "productId",
        sortable: true,
        aggFunc: "unique",
        valueFormatter: (params) => {
          return params.node?.group
            ? params.value?.length > 1
              ? `${params.value?.length} products`
              : first(params.value)
            : params.value
        },
        tooltipComponent: UniqueListTooltip,
        tooltipComponentParams: { title: "Products", tooltipType: "UniqueList" },
        tooltip: (params) => params.value,
      },
      {
        headerName: "Product Name",
        field: "productName",
        sortable: true,
        rowGroup: AggregationMethod.ProductName === aggregation,
        aggFunc: "unique",
        valueFormatter: (params) => {
          return params.node?.group
            ? params.value?.length > 1
              ? `${params.value?.length} names`
              : first(params.value)
            : params.value
        },
        tooltipComponent: UniqueListTooltip,
        tooltipComponentParams: { title: "Product names", tooltipType: "UniqueList" },
        tooltip: (params) => params.value,
      },
      {
        headerName: "Category",
        field: "category",
        sortable: true,
        rowGroup: AggregationMethod.Category === aggregation,
        aggFunc: "unique",
        valueFormatter: (params) => {
          return params.node?.group
            ? params.value?.length > 1
              ? `${params.value?.length} categories`
              : first(params.value)
            : params.value
        },
        tooltipComponent: UniqueListTooltip,
        // eslint-disable-next-line @hakio/no-untranslated-terminology -- internal tool, no tenant naming needed
        tooltipComponentParams: { title: "Categories", tooltipType: "UniqueList" },
        tooltip: (params) => params.value,
      },
      {
        headerName: "New Min Date",
        field: "new_min_date",
        sortable: true,
        valueFormatter: (params) => (params.value ? formatDate(params.value) : "-"),
        aggFunc: "min",
      },
      {
        headerName: "New Max Date",
        field: "new_max_date",
        sortable: true,
        valueFormatter: (params) => (params.value ? formatDate(params.value) : "-"),
        aggFunc: "max",
      },
      {
        headerName: "New Forecast",
        field: "new_forecast",
        sortable: true,
        valueFormatter: (params) => (params.value ? formatNumber(params.value, 0) : "-"),
        aggFunc: "sum",
      },
      {
        headerName: "New Model",
        field: "new_model",
        sortable: true,
        aggFunc: "unique",
        valueFormatter: (params) => {
          return params.node?.group
            ? params.value?.length > 1
              ? `${params.value?.length} models`
              : first(params.value)
            : params.value
        },
        tooltipComponent: UniqueListTooltip,
        tooltipComponentParams: { title: "Models", tooltipType: "UniqueList" },
        tooltip: (params) => params.value,
      },
      {
        headerName: "New Date Count",
        field: "new_dates",
        sortable: true,
        valueFormatter: (params) => (params.value ? formatNumber(Number(params.value), 1) : "-"),
        cellDataType: "number",
        aggFunc: "avg",
      },

      {
        headerName: "Old Min Date",
        field: "old_min_date",
        sortable: true,
        valueFormatter: (params) => (params.value ? formatDate(params.value) : "-"),
        aggFunc: "min",
      },
      {
        headerName: "Old Max Date",
        field: "old_max_date",
        sortable: true,
        valueFormatter: (params) => (params.value ? formatDate(params.value) : "-"),
        aggFunc: "max",
      },
      {
        headerName: "Old Forecast",
        field: "old_forecast",
        sortable: true,
        valueFormatter: (params) => (params.value ? formatNumber(params.value, 0) : "-"),
        aggFunc: "sum",
      },
      {
        headerName: "Old Model",
        field: "old_model",
        aggFunc: "unique",
        valueFormatter: (params) => {
          return params.node?.group
            ? params.value?.length > 1
              ? `${params.value?.length} models`
              : first(params.value)
            : params.value
        },
        tooltipComponent: UniqueListTooltip,
        tooltipComponentParams: { title: "Models", tooltipType: "UniqueList" },
        tooltip: (params) => params.value,
      },
      {
        headerName: "Old Date Count",
        field: "old_dates",
        sortable: true,
        valueFormatter: (params) => (params.value ? formatNumber(Number(params.value), 1) : "-"),
        cellDataType: "number",
        aggFunc: "avg",
      },
      {
        headerName: "Change",
        sortable: true,
        filter: "agNumberColumnFilter",
        filterParams: {
          buttons: ["clear"],
          filterOptions: [
            "lessThan",
            "lessThanOrEqual",
            "greaterThan",
            "greaterThanOrEqual",
            "inRange",
          ],
        },
        valueGetter: (params) => {
          const newForecast = params.data?.new_forecast
          const oldForecast = params.data?.old_forecast

          return Math.abs((newForecast ?? 0) - (oldForecast ?? 0))
        },
        valueFormatter: (params) => (params.value != null ? formatNumber(params.value, 0) : "-"),
        cellDataType: "number",
        aggFunc: "sum",
      },
      {
        headerName: "Change %",
        sortable: true,
        filter: "agNumberColumnFilter",
        filterParams: {
          buttons: ["clear"],
          filterOptions: [
            "lessThan",
            "lessThanOrEqual",
            "greaterThan",
            "greaterThanOrEqual",
            "inRange",
          ],
        },
        valueGetter: (params) => {
          const newForecast = params.data?.new_forecast
          const oldForecast = params.data?.old_forecast

          if (newForecast == null || oldForecast == null || oldForecast === 0) {
            return null
          }

          return ((newForecast - oldForecast) / oldForecast) * 100
        },
        valueFormatter: (params) =>
          params.value != null ? `${formatNumber(params.value, 1)}%` : "-",
        cellStyle: (params) => ({
          color:
            params.value == null
              ? "inherit"
              : params.value > 0
                ? getTailwindValue("--color-green-600")
                : params.value < 0
                  ? getTailwindValue("--color-red-500")
                  : "inherit",
        }),
        cellDataType: "number",
        aggFunc: (params) => {
          const values = params.values.filter((value) => value != null)
          return mean(values)
        },
      },
    ]
  }, [validationChecks, aggregation])

  const gridOptions: GridOptions<IForecastComparison> = {
    getRowStyle: (params) => {
      if (params.node.footer) {
        return { fontWeight: 600 }
      }
    },
  }

  return (
    <Module
      breadcrumb={
        <BreadcrumbWrapper
          items={[
            { label: "Internal", href: "/internal" },
            { label: "Forecasts" },
            { label: "Validation" },
          ]}
        />
      }
    >
      <WorkspacePage
        toolbar={
          <div className="flex gap-2">
            <AggregationSelector
              selectedMethod={aggregation}
              aggregationMethods={aggregationOptions}
              onSelect={(method) => setAggregation(method)}
            />
            <SearchField
              initialSearchText={searchText}
              onSearchTextChange={setSearchText}
              placeholder="Search variants..."
            />
          </div>
        }
      >
        <DataTableBaseV2<IForecastComparison>
          id="forecast-validation-check"
          gridRef={internalGridRef}
          rowData={filteredRowData ?? []}
          rowIdField="variantId"
          defaultColumnDef={defaultColumnDef}
          autoGroupColumnDef={autoGroupColumnDef}
          columnDefs={colDefs}
          gridOptions={gridOptions}
          emptyMessage="No forecasts have failed the validation check"
          grandTotalRow="pinnedBottom"
          loading={validationChecks === undefined || isLoading}
        />
      </WorkspacePage>
    </Module>
  )
}
